import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Suspense, lazy } from "react";
import React from "react";
import { Toaster } from "react-hot-toast";
import { MainLayout } from "@/components/layout/MainLayout";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { PermissionProvider } from "@/contexts/PermissionContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleProtectedRoute from "@/components/RoleProtectedRoute";
import RoleProtectedSalesManager from "@/components/RoleProtectedSalesManager";

import {
  ReportingGuard,
  SettingsGuard,
  UserManageGuard,
  PermissionGuard,
  AdminGuard,
  SuperadminGuard,
} from "@/components/PermissionGuard";
import OnboardingProtectedRoute from "@/components/OnboardingProtectedRoute";
import { supabase } from "@/lib/supabaseClient";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BookingConfirmation from "./features/site-survey/pages/BookingConfirmation";

// Component to handle global auth events inside router context
function AuthEventHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthSignedOut = () => {
      navigate('/login');
    };

    window.addEventListener('auth-signed-out', handleAuthSignedOut);
    return () => window.removeEventListener('auth-signed-out', handleAuthSignedOut);
  }, [navigate]);

  return null; // This component only handles events
}
import SiteSurveyV3 from "./features/site-survey/pages/SiteSurvey3";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TasksPage } from "@/pages/Tasks";
import { TooltipProvider } from "@/components/ui/tooltip";
import WelcomeNextSteps from "./pages/WelcomeNextSteps";
import { SignContractPage } from "@/pages/SignContractPage";
import Settings from "./pages/Settings";
import { ThemeTest } from "./components/ThemeTest";
import { PageLoadingSpinner } from "@/components/ui/LoadingSpinner";
import RLSTestPage from "./pages/RLSTestPage";
import { PostHogIdentificationProvider } from "@/components/PostHogIdentificationProvider";
import Impersonate from "./pages/Impersonate";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DefaultDashboardRedirect } from "@/components/DefaultDashboardRedirect";

// Lazy load pages
const HomePage = lazy(() =>
  import("@/pages/HomePage").then((module) => ({ default: module.HomePage }))
);
const DashboardView = lazy(() =>
  import("@/pages/DashboardView").then((module) => ({ default: module.DashboardView }))
);
const ProjectDetail = lazy(() =>
  import("@/pages/ProjectDetail").then((module) => ({
    default: module.ProjectDetail,
  }))
);
const Pipeline = lazy(() =>
  import("@/pages/Pipeline").then((module) => ({ default: module.Pipeline }))
);
const PipelineResi = lazy(() =>
  import("@/pages/PipelineResi").then((module) => ({
    default: module.PipelineResi,
  }))
);
const Contracts = lazy(() => import("@/pages/Contracts"));
const Login = lazy(() =>
  import("@/pages/Login").then((module) => ({ default: module.Login }))
);
const ForgotPassword = lazy(() =>
  import("@/pages/ForgotPassword").then((module) => ({
    default: module.ForgotPassword,
  }))
);
const ResetPassword = lazy(() =>
  import("@/pages/ResetPassword").then((module) => ({
    default: module.ResetPassword,
  }))
);
const NewProject = lazy(() => import("@/pages/NewProject"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SetupStages = lazy(() => import("@/pages/SetupStages"));

const Support = lazy(() => import("@/pages/Support"));
const SupportChat = lazy(() => import("@/pages/SupportChat"));
const SupportTickets = lazy(() => import("@/pages/SupportTickets"));
const TicketDetail = lazy(() => import("@/pages/TicketDetail"));
const NewTicket = lazy(() => import("@/pages/NewTicket"));
const DailyDebrief = lazy(() => import("@/pages/DailyDebrief"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const IntegrationCallback = lazy(() => import("@/pages/IntegrationCallback"));
const Reporting = lazy(() =>
  import("@/pages/Reporting").then((module) => ({ default: module.Reporting }))
);
const Notifications = lazy(() => import("@/pages/Notifications"));
const Map = lazy(() => import("@/pages/Map"));
const Redlines = lazy(() => import("@/pages/Redlines"));
const CommercialRedlines = lazy(() => import("@/pages/CommercialRedlines"));
const SESRedlines = lazy(() => import("@/pages/SESRedlines"));
const Signup = lazy(() =>
  import("@/pages/Signup").then((module) => ({ default: module.Signup }))
);
const AddUserPage = lazy(() => import("@/pages/AddUserPage"));
const Tracking = lazy(() =>
  import("@/pages/Tracking").then((module) => ({ default: module.Tracking }))
);
const AuroraIframe = lazy(() => import("@/pages/AuroraIframe"));
const OnboardingBookingPage = lazy(
  () => import("@/pages/OnboardingBookingPage")
);
const LandingPageV1 = lazy(() => import("@/pages/LandingPageV1"));
const MobileSupport = lazy(() => import("@/pages/MobileSupport"));
const TermsPage = lazy(() =>
  import("@/pages/TermsPage").then((module) => ({ default: module.TermsPage }))
);
const OnboardingPipeline = lazy(() => import("@/pages/OnboardingPipeline"));

const FAQ = lazy(() => import("@/pages/FAQ"));
const PlaylistManager = lazy(() => import("@/pages/PlaylistManager"));
const VideoManager = lazy(() => import("@/pages/VideoManager"));
const RequiredTraining = lazy(() => import("@/pages/RequiredTraining"));
const Empower = lazy(() =>
  import("@/pages/Empower").then((module) => ({ default: module.Empower }))
);
const Integrations = lazy(() => import("@/pages/Integrations"));
const AutomationBuilder = lazy(() =>
  import("@/pages/AutomationBuilder").then((module) => ({
    default: module.AutomationBuilder,
  }))
);
const UpdateNotificationDemo = lazy(() => import("@/pages/UpdateNotificationDemo"));

// Spike components (development only)
const DrawerSpike = lazy(() =>
  import("@/spike/DrawerSpike").then((module) => ({
    default: module.DrawerSpike
  }))
);

// Software Stack Assessment pages
const PublicSoftwareStackAssessment = lazy(() => 
  import("@/pages/PublicSoftwareStackAssessment").then((module) => ({ 
    default: module.PublicSoftwareStackAssessment 
  }))
);
const SoftwareStackResults = lazy(() => 
  import("@/pages/SoftwareStackResults").then((module) => ({ 
    default: module.SoftwareStackResults 
  }))
);

// Booking Links pages
const PublicBookingPage = lazy(() => import("@/pages/PublicBookingPage"));
const BookingConfirmationPage = lazy(
  () => import("@/pages/BookingConfirmationPage")
);
const BookingTest = lazy(() => import("@/pages/BookingTest"));
const BookingRequests = lazy(() => import("@/pages/BookingRequests"));
const BookingLinkDiagnostic = lazy(
  () => import("@/pages/BookingLinkDiagnostic")
);

// Customer Portal
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const CustomerPortalAuth = lazy(() => import("@/pages/CustomerPortalAuth"));
const CustomerPortalFiles = lazy(() => import("@/pages/CustomerPortalFiles"));

// Loading component
const LoadingFallback = () => <PageLoadingSpinner message="Loading..." />;

// Protected route with password reset check
const ProtectedRouteWithResetCheck = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          navigate("/login");
          return;
        }

        // Add a small delay to ensure the session is fully initialized
        await new Promise((resolve) => setTimeout(resolve, 100));

        const { data: userData, error } = await supabase
          .from("users")
          .select("force_password_reset, onboarding_required")
          .eq("id", session.user.id)
          .single();

        if (error) {
          navigate("/login");
          return;
        }

        if (userData?.force_password_reset === true) {
          setNeedsPasswordReset(true);
          navigate("/reset-password");
          return;
        }

        if (userData?.onboarding_required === true) {
          setNeedsOnboarding(true);
          return;
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        navigate("/login");
      } finally {
        setIsLoading(false);
      }
    };

    checkUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate effect to handle onboarding redirect when location changes
  useEffect(() => {
    if (!isLoading && needsOnboarding && !location.pathname.startsWith("/required-training")) {
      const checkAndRedirect = async () => {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          navigate('/required-training');
        }
      };
      checkAndRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, needsOnboarding, isLoading]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (needsPasswordReset || needsOnboarding) {
    return null;
  }

  return <>{children}</>;
};

// Create QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <AuthProvider>
          <TenantProvider>
            <PermissionProvider>
              <ThemeProvider>
                <SidebarProvider>
                  <PostHogIdentificationProvider>
                  <AuthEventHandler />
                  <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Navigate to="/home" replace />} />
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/home-test" element={<LandingPageV1 />} />
                    <Route path="/mobile-support" element={<MobileSupport />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/sign" element={<SignContractPage />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/impersonate" element={<Impersonate />} />
                    <Route
                      path="/integration-callback"
                      element={<IntegrationCallback />}
                    />
                    <Route
                      path="/integrations"
                      element={<Integrations />}
                    />
                    <Route path="/signup" element={<Signup />} />
                    <Route
                      path="/welcome-next-steps"
                      element={<WelcomeNextSteps />}
                    />
                    <Route
                      path="/onboarding-booking"
                      element={<OnboardingBookingPage />}
                    />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/required-training" element={<RequiredTraining />} />

                    {/* Public booking routes */}
                    <Route path="/book/:slug" element={<PublicBookingPage />} />
                    <Route
                      path="/booking-confirmation/:slug"
                      element={<BookingConfirmationPage />}
                    />
                    <Route path="/booking-test" element={<BookingTest />} />
                    <Route
                      path="/booking-diagnostic/:slug"
                      element={<BookingLinkDiagnostic />}
                    />

                    {/* Public AI audit assessment routes */}
                    <Route path="/ai-audit" element={<PublicSoftwareStackAssessment />} />
                    <Route path="/ai-audit/results" element={<SoftwareStackResults />} />

                    {/* Public customer portal routes */}
                    <Route path="/customer/:projectId" element={<CustomerPortal />} />
                    <Route path="/customer/:projectId/auth" element={<CustomerPortalAuth />} />
                    <Route path="/customer/:projectId/portal" element={<CustomerPortalFiles />} />

                    {/* Protected routes under /app */}
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <ProtectedRouteWithResetCheck>
                            <MainLayout />
                          </ProtectedRouteWithResetCheck>
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={<DefaultDashboardRedirect />}
                      />
                      <Route path="dashboard" element={<DefaultDashboardRedirect />} />
                      <Route path="dashboards/:id" element={<DashboardView />} />

                      <Route
                        path="redlines"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_projects",
                              "project.view_all",
                              "project.view_team",
                            ]}
                          >
                            <Redlines />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="commercial-redlines"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_commercial_pipeline",
                              "view_projects",
                              "project.view_all",
                            ]}
                          >
                            <CommercialRedlines />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="ses-redlines"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_projects",
                              "project.view_all",
                              "project.view_team",
                            ]}
                          >
                            <SESRedlines />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="tasks"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_projects",
                              "manage_projects",
                              "edit_projects",
                            ]}
                          >
                            <TasksPage />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="projects/:projectId"
                        element={<ProjectDetail />}
                      />
                      <Route path="pipeline" element={<Pipeline />} />
                      <Route path="pipeline-resi" element={<PipelineResi />} />
                      <Route path="empower" element={<Empower />} />
                      <Route path="aurora" element={<AuroraIframe />} />
                      <Route
                        path="reporting"
                        element={
                          <ReportingGuard>
                            <Reporting />
                          </ReportingGuard>
                        }
                      />
                      <Route
                        path="contracts"
                        element={
                          <RoleProtectedRoute>
                            <Contracts />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="tracking"
                        element={
                          <PermissionGuard permission="reporting.admin">
                            <Tracking />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <SettingsGuard>
                            <Settings />
                          </SettingsGuard>
                        }
                      />
                      <Route
                        path="theme-test"
                        element={
                          <RoleProtectedRoute>
                            <ThemeTest />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="update-notification-demo"
                        element={
                          <RoleProtectedRoute>
                            <UpdateNotificationDemo />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="drawer-spike"
                        element={
                          <RoleProtectedRoute>
                            <DrawerSpike />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="automation-builder/:automationId"
                        element={
                          <RoleProtectedRoute>
                            <AutomationBuilder />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route path="new-project" element={<NewProject />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route
                        path="add-user"
                        element={
                          <UserManageGuard>
                            <AddUserPage />
                          </UserManageGuard>
                        }
                      />
                      <Route path="setup-stages" element={<SetupStages />} />
                      <Route path="support" element={<Support />} />
                      <Route path="support-chat" element={<SupportChat />} />
                      <Route
                        path="support-tickets"
                        element={<SupportTickets />}
                      />
                      <Route
                        path="support-tickets/new"
                        element={<NewTicket />}
                      />
                      <Route
                        path="support-tickets/:ticketId"
                        element={
                          <ErrorBoundary>
                            <TicketDetail />
                          </ErrorBoundary>
                        }
                      />
                      <Route path="faq" element={<FAQ />} />
                      <Route
                        path="playlist-manager"
                        element={
                          <RoleProtectedRoute>
                            <PlaylistManager />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="video-manager"
                        element={
                          <RoleProtectedRoute>
                            <VideoManager />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route path="daily-debrief" element={<DailyDebrief />} />
                      <Route path="notifications" element={<Notifications />} />

                      {/* Scheduler routes */}
                      <Route
                        path="scheduler"
                        element={<Navigate to="appointments" replace />}
                      />
                      <Route
                        path="scheduler/appointments"
                        element={<Schedule />}
                      />
                      <Route
                        path="scheduler/event-management"
                        element={<Schedule />}
                      />
                      <Route
                        path="scheduler/availability"
                        element={<Schedule />}
                      />
                      <Route
                        path="scheduler/booking-links"
                        element={
                          <AdminGuard>
                            <Schedule />
                          </AdminGuard>
                        }
                      />
                      <Route
                        path="scheduler/booking-links/requests"
                        element={
                          <AdminGuard>
                            <BookingRequests />
                          </AdminGuard>
                        }
                      />

                      {/* Redirect old events-management path */}
                      <Route
                        path="events-management"
                        element={
                          <Navigate to="scheduler/event-management" replace />
                        }
                      />

                      {/* Site Survey Routes */}
                      <Route path="sitesurvey" element={<SiteSurveyV3 />} />
                      <Route
                        path="sitesurvey/booking-confirmation/:id"
                        element={<BookingConfirmation />}
                      />
                      <Route
                        path="booking-confirmation"
                        element={<BookingConfirmation />}
                      />
                      <Route path="map" element={<Map />} />

                      {/* Only redirect unknown routes that are not onboarding */}
                      <Route
                        path="onboarding"
                        element={
                          <OnboardingProtectedRoute>
                            <OnboardingPipeline />
                          </OnboardingProtectedRoute>
                        }
                      />
                      <Route
                        path="rls-test"
                        element={
                          <SuperadminGuard>
                            <RLSTestPage />
                          </SuperadminGuard>
                        }
                      />
                      <Route
                        path=":rest"
                        element={<Navigate to="/app/dashboard" replace />}
                      />
                    </Route>

                    {/* Redirect old routes to new /app prefixed routes */}
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/app/dashboard" replace />}
                    />
                    <Route
                      path="/redlines"
                      element={<Navigate to="/app/redlines" replace />}
                    />
                    <Route
                      path="/projects/:projectId"
                      element={
                        <Navigate to="/app/projects/:projectId" replace />
                      }
                    />
                    <Route
                      path="/pipeline"
                      element={<Navigate to="/app/pipeline" replace />}
                    />
                    <Route
                      path="/pipeline-resi"
                      element={<Navigate to="/app/pipeline-resi" replace />}
                    />
                    <Route
                      path="/empower"
                      element={<Navigate to="/app/empower" replace />}
                    />
                    <Route
                      path="/aurora"
                      element={<Navigate to="/app/aurora" replace />}
                    />
                    <Route
                      path="/reporting"
                      element={<Navigate to="/app/reporting" replace />}
                    />
                    <Route
                      path="/contracts"
                      element={<Navigate to="/app/contracts" replace />}
                    />
                    <Route
                      path="/settings"
                      element={<Navigate to="/app/settings" replace />}
                    />
                    <Route
                      path="/new-project"
                      element={<Navigate to="/app/new-project" replace />}
                    />
                    <Route
                      path="/profile"
                      element={<Navigate to="/app/profile" replace />}
                    />
                    <Route
                      path="/setup-stages"
                      element={<Navigate to="/app/setup-stages" replace />}
                    />
                    <Route
                      path="/support"
                      element={<Navigate to="/app/support" replace />}
                    />
                    <Route
                      path="/support-chat"
                      element={<Navigate to="/app/support-chat" replace />}
                    />
                    <Route
                      path="/support-tickets"
                      element={<Navigate to="/app/support-tickets" replace />}
                    />
                    <Route
                      path="/support-tickets/new"
                      element={
                        <Navigate to="/app/support-tickets/new" replace />
                      }
                    />
                    <Route
                      path="/support-tickets/:ticketId"
                      element={
                        <Navigate to="/app/support-tickets/:ticketId" replace />
                      }
                    />
                    <Route
                      path="/daily-debrief"
                      element={<Navigate to="/app/daily-debrief" replace />}
                    />
                    <Route
                      path="/scheduler"
                      element={<Navigate to="/app/scheduler" replace />}
                    />
                    <Route
                      path="/scheduler/*"
                      element={<Navigate to="/app/scheduler" replace />}
                    />
                    <Route
                      path="/events-management"
                      element={<Navigate to="/app/events-management" replace />}
                    />
                    <Route
                      path="/sitesurvey"
                      element={<Navigate to="/app/sitesurvey" replace />}
                    />
                    <Route
                      path="/sitesurvey/booking-confirmation/:id"
                      element={
                        <Navigate
                          to="/app/sitesurvey/booking-confirmation/:id"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/booking-confirmation"
                      element={
                        <Navigate to="/app/booking-confirmation" replace />
                      }
                    />
                    <Route
                      path="/map"
                      element={<Navigate to="/app/map" replace />}
                    />
                    <Route
                      path="/faq"
                      element={<Navigate to="/app/faq" replace />}
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </Suspense>
                  </PostHogIdentificationProvider>
                </SidebarProvider>
              </ThemeProvider>
            </PermissionProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
